/**
 * One directory read per distinct query, keyed so that a late answer to an
 * older query can never replace a newer one (the `OrderingContextProvider`
 * pattern: the answer is kept WITH the key it answers, and "loading" is
 * derived, not set inside the effect).
 */

import { useEffect, useState } from "react";
import type { ApiClient } from "../api/client.js";
import { readDirectory } from "./locationsApi.js";
import type { DirectoryQuery, DirectoryResult } from "./types.js";

export interface DirectoryAnswer {
  /** The query this answers; a new object per answer, so effects can key on it. */
  readonly key: string;
  readonly result: DirectoryResult;
}

/** `null` query means "do not read". Returns the answer for THIS query, or null while it is in flight. */
export function useDirectory(
  client: ApiClient,
  brandNodeId: string,
  query: DirectoryQuery | null,
  attempt: number,
): DirectoryAnswer | null {
  const key = query === null ? "" : JSON.stringify([brandNodeId, query, attempt]);
  const [answer, setAnswer] = useState<DirectoryAnswer | null>(null);

  useEffect(() => {
    if (key === "") return;
    const [brand, parsed] = JSON.parse(key) as [string, DirectoryQuery];
    let live = true;
    void readDirectory(client, brand, parsed).then((result) => {
      if (live) setAnswer({ key, result });
    });
    return () => {
      live = false;
    };
  }, [client, key]);

  return answer !== null && answer.key === key ? answer : null;
}
